import React, { useContext, useEffect, useState } from "react";
import { companyConfig } from "../myGiftCardList";
import {
  downloadBarcodeRedemption,
  listAllGiftCardRedemption,
} from "../../../reducers/payouts.slice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { decodeHashData } from "../../../utils/hashing";
import { downloadDocument } from "../../../utils/util";
import { AuthContext } from "../../../context/authContext";
import { ArrowLeft } from "lucide-react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";

/* ── card tile helpers ─────────────────────────────────────── */
const cardLogoWrapperStyle = (bg) => ({
  background: "linear-gradient(rgb(0 0 0 / 27%) 20%, rgb(0, 0, 0) 100%)",
  backgroundColor: bg || "#000000",
  borderRadius: 4,
  height: 200,
  position: "relative",
});

const purchasedInfoStyle = {
  background: "#000000B8",
  boxShadow: "3px 1px 20px 1px rgba(0,0,0,0.27)",
  textAlign: "center",
  height: 30,
  padding: "4px 8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const infoTextStyle = {
  fontSize: "0.8rem",
  letterSpacing: 1,
  textTransform: "uppercase",
  color: "#FFFFFFB8",
  fontWeight: 600,
  fontFamily: "Arial",
};

const cardLogoFrameStyle = {
  height: 150,
  padding: "48px 59px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const cardDetailsStyle = {
  height: 128,
  padding: "6px 16px 24px 16px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const cardTypeLabel = (types) =>
  types?.length === 1 ? types[0] : types?.join(" & ")?.replace(/_/g, " ");

const retailerLabel = (r) =>
  r?.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

/* ── MyGiftCards ───────────────────────────────────────────── */
const MyGiftCards = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { showToast } = useContext(AuthContext);
  const [showLoader, setShowLoader] = useState(false);
  const [storeCardData, setStoreCardData] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [openStoreCard, setOpenStoreCard] = useState(false);
  const [openCardProcessing, setOpenCardProcessing] = useState(false);
  const [imageBase64, setImageBase64] = useState("");
  const [openRejectedCard, setOpenRejectedCard] = useState(false);

  useEffect(() => {
    setShowLoader(true);
    dispatch(listAllGiftCardRedemption()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      const formattedGiftCard = res?.payload?.map((item) => ({
        ...item,
        created_at: dayjs(item?.created_at).format("MM/DD/YYYY"),
        gift_card_code: decodeHashData(item?.gift_card_code),
        gift_card_pin: decodeHashData(item?.gift_card_pin),
      }));
      setStoreCardData(formattedGiftCard);
      setShowLoader(false);
    });
  }, []);

  const handleStoreCardOpen = async (card) => {
    const enrichedGiftCard = {
      ...card,
      ...(companyConfig[card?.gift_card_details?.retailer] || {
        logo: "/logos/default.png",
        backgroundColor: "#ccc",
      }),
    };

    if (card?.status === "APPROVED" || card?.status === "PENDING_APPROVAL") {
      setSelectedCard(enrichedGiftCard);
      setOpenCardProcessing(true);
    } else if (card?.status === "REJECTED") {
      setSelectedCard(enrichedGiftCard);
      setOpenRejectedCard(true);
    } else {
      const response = await dispatch(downloadBarcodeRedemption({ id: card?.id }));
      if (response?.error && Object.keys(response?.error)?.length > 0) {
        showToast(response?.error?.message, "error");
        return;
      }
      setImageBase64(
        response?.payload?.length === 0
          ? ""
          : `data:${response?.payload[0]?.content_type};base64,${response?.payload[0]?.document_bytes}`
      );
      setSelectedCard(enrichedGiftCard);
      setOpenStoreCard(true);
    }
  };

  return (
    <>
      <div style={{ padding: "0 16px" }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.52)", cursor: "pointer", marginTop: 24, marginBottom: 16 }}
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />
          <span style={{ fontFamily: "inter", fontSize: 14 }}>Back</span>
        </div>

        <div style={{ marginBottom: 16, marginTop: 8 }}>
          <h2 style={{ fontSize: "1.4rem", fontWeight: 700, margin: "0 0 4px 0" }}>My Gift Cards</h2>
          <p style={{ color: "rgba(255,255,255,0.52)", fontFamily: "Inter", fontWeight: 300, fontSize: 15, margin: 0 }}>
            Keep track of all your Gift Card purachases here.
          </p>
        </div>
        <br />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
          {storeCardData?.map((card, index) => {
            const { logo, bg } = companyConfig[card?.gift_card_details?.retailer] || { logo: "/images/amazon.png", bg: "#000000" };
            return (
              <div
                key={index}
                onClick={() => handleStoreCardOpen(card)}
                style={{ padding: 8, borderRadius: 10, border: "1px solid #3C3C3C", cursor: "pointer" }}
              >
                <div style={{ width: "100%", height: 350, borderRadius: 10, display: "flex", flexDirection: "column" }}>
                  <div style={cardLogoWrapperStyle(bg)}>
                    <div style={purchasedInfoStyle}>
                      <div style={infoTextStyle}>
                        {card?.status === "READY_FOR_REDEMPTION"
                          ? `PURCHASED: ${card?.created_at}`
                          : card?.status === "REJECTED"
                          ? "REJECTED"
                          : "PROCESSING"}
                      </div>
                    </div>
                    <div style={cardLogoFrameStyle}>
                      <div>
                        <img src={logo} alt={card.retailer} style={{ width: 125, height: 40, objectFit: "contain" }} />
                      </div>
                    </div>
                  </div>
                  <div style={cardDetailsStyle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
                      <span style={{ fontWeight: 400, fontSize: "0.9rem", lineHeight: "100%", letterSpacing: 2, textTransform: "uppercase" }}>
                        {cardTypeLabel(card?.gift_card_details?.gift_card_type)}
                      </span>
                      <span style={{ fontWeight: 700, fontSize: "1.4rem", letterSpacing: 2, display: "block" }}>
                        {retailerLabel(card?.gift_card_details?.retailer)}
                      </span>
                    </div>
                    <div style={{ height: 24, marginTop: 10 }}>
                      <p style={{ height: 23, fontWeight: 400, fontSize: "1.7rem", letterSpacing: 0, color: "hsl(0 0% 70% / 1)", margin: 0 }}>
                        $ {card?.discounted_amount}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Dialog: Store Card (Ready for Redemption) ─────────── */}
      <Dialog open={openStoreCard} onOpenChange={setOpenStoreCard}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCard?.gift_card_details?.retailer?.replace(/_/g, " ")}</DialogTitle>
          </DialogHeader>
          <div style={{ border: "1px solid #3C3C3C", borderRadius: 10, padding: 8 }}>
            <div style={{ background: "linear-gradient(rgb(0 0 0 / 27%) 20%, rgb(0, 0, 0) 100%)", backgroundColor: selectedCard?.bg, borderRadius: 4 }}>
              <div style={purchasedInfoStyle}>
                <div style={infoTextStyle}>
                  {selectedCard?.status === "READY_FOR_REDEMPTION"
                    ? `PURCHASED: ${selectedCard?.created_at}`
                    : "PROCESSING"}
                </div>
              </div>
              <div>
                <div style={{ textAlign: "center", paddingTop: 30, height: 100 }}>
                  <img src={selectedCard?.logo} alt={selectedCard?.retailer} style={{ height: 32 }} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", justifyContent: "center", padding: "8px 0" }}>
                  <span style={{ fontSize: "1.3rem", letterSpacing: 2 }}>{selectedCard?.gift_card_code}</span>
                  <span style={{ fontSize: "1.1rem", letterSpacing: 2 }}>PIN: {selectedCard?.gift_card_pin}</span>
                  {imageBase64 && (
                    <div style={{
                      height: 100, width: 280, margin: "20px auto",
                      backgroundImage: `url(${imageBase64})`,
                      backgroundPosition: "center",
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      borderRadius: 4,
                    }} />
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", padding: "0 30px", height: 84 }}>
                  <span style={{ fontWeight: 400, fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>
                    {cardTypeLabel(selectedCard?.gift_card_details?.gift_card_type)}
                  </span>
                  <span style={{ fontSize: "1.1rem", letterSpacing: 2 }}>${selectedCard?.discounted_amount}</span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 16, justifyContent: "space-between" }}>
            <Button
              variant="outline"
              style={{ flex: 1 }}
              onClick={() => {
                if (selectedCard?.gift_card_code) {
                  navigator.clipboard.writeText(selectedCard.gift_card_code)
                    .then(() => toast("Gift card code copied to clipboard!", { type: "success", autoClose: 2000 }))
                    .catch(() => toast("Failed to copy code", { type: "error" }));
                } else {
                  toast("No gift card code available", { type: "warning" });
                }
              }}
            >
              Copy Code
            </Button>
            <Button
              variant="outline"
              style={{ flex: 1 }}
              disabled={!imageBase64}
              onClick={() => {
                try {
                  const base64Data = imageBase64.includes("data:") ? imageBase64.split(",")[1] : imageBase64;
                  downloadDocument(base64Data, `${selectedCard?.gift_card_code || "barcode"}.png`);
                } catch {
                  showToast("Unable to download barcode image", { type: "error" });
                }
              }}
            >
              Download Barcode
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Card Processing ───────────────────────────── */}
      <Dialog open={openCardProcessing} onOpenChange={setOpenCardProcessing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCard?.gift_card_details?.retailer?.replace(/_/g, " ")}</DialogTitle>
          </DialogHeader>
          <div style={{ border: "1px solid #3C3C3C", borderRadius: 10, padding: 8 }}>
            <div style={{ background: "linear-gradient(rgb(0 0 0 / 27%) 20%, rgb(0, 0, 0) 100%)", backgroundColor: selectedCard?.bg, borderRadius: 4 }}>
              <div style={{ textAlign: "center", paddingTop: 30, height: 100 }}>
                <img src={selectedCard?.logo} alt={selectedCard?.retailer} style={{ height: 32 }} />
              </div>
              <div style={{ height: 80, textAlign: "center", padding: "8px 16px" }}>
                <span style={{ fontSize: "1.1rem", letterSpacing: 1 }}>
                  We are processing your request. It might take upto 72hrs to deliver your card details.
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", padding: "0 30px", height: 84 }}>
                <span style={{ fontWeight: 400, fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>
                  {cardTypeLabel(selectedCard?.gift_card_details?.gift_card_type)}
                </span>
                <span style={{ fontSize: "1.1rem", letterSpacing: 2 }}>${selectedCard?.discounted_amount}</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Rejected Card ─────────────────────────────── */}
      <Dialog open={openRejectedCard} onOpenChange={setOpenRejectedCard}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCard?.gift_card_details?.retailer?.replace(/_/g, " ")}</DialogTitle>
          </DialogHeader>
          <div style={{ border: "1px solid #3C3C3C", borderRadius: 10, padding: 8 }}>
            <div style={{ background: "linear-gradient(rgb(0 0 0 / 27%) 20%, rgb(0, 0, 0) 100%)", backgroundColor: selectedCard?.bg, borderRadius: 4 }}>
              <div style={{ textAlign: "center", paddingTop: 30, height: 100 }}>
                <img src={selectedCard?.logo} alt={selectedCard?.retailer} style={{ height: 32 }} />
              </div>
              <div style={{ height: 80, textAlign: "center", padding: "8px 16px" }}>
                <p style={{ fontSize: "1rem", letterSpacing: 1, margin: "0 0 4px 0" }}>
                  The gift card request has been rejected
                </p>
                <p style={{ fontSize: "0.85rem", letterSpacing: 1, margin: 0 }}>
                  Reason: {selectedCard?.notes}
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MyGiftCards;
